import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles, Zap, BarChart3, Target, Hash, Users, TrendingUp,
  Crown, Instagram, Star, ChevronDown, Lock, FileText, Activity,
} from "lucide-react";
import Nav from "@/components/Nav";
import AmbientBg from "@/components/AmbientBg";
import { PromoNote } from "@/lib/pricing";

const FEATURES = [
  { icon: Activity, title: "Engagement Quality", body: "Real engagement vs vanity. We score authenticity, comment depth, save-rate." },
  { icon: Target,   title: "Brand Score",        body: "Identity strength, visual consistency, recognizability — like an agency audit." },
  { icon: Zap,      title: "Viral Hook Engine",  body: "AI generates 8+ scroll-stopping hooks tailored to your niche." },
  { icon: Hash,     title: "Hashtag Intelligence", body: "Find niche-winning hashtags and ditch the overused ones." },
  { icon: BarChart3, title: "Best Posting Time", body: "Hour-by-hour heatmap of when your audience is online." },
  { icon: Users,    title: "Competitor Benchmark", body: "How you stack against the top 50 in your category." },
  { icon: TrendingUp, title: "30-Day Roadmap",   body: "Week-by-week action plan to hit your next growth milestone." },
  { icon: Crown,    title: "Premium PDF Report", body: "A beautifully designed PDF you can share with your team or client." },
];

const TESTIMONIALS = [
  {
    name: "Aanya Mehra", role: "Founder · DTC Skincare",
    avatar: "https://images.pexels.com/photos/16597254/pexels-photo-16597254.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=200&w=200",
    quote: "Spotted issues with our reel hooks in 90 seconds that 3 agencies missed in 3 months.",
  },
  {
    name: "Rohan Singh", role: "Content Creator · 220k",
    avatar: "https://images.pexels.com/photos/11230995/pexels-photo-11230995.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=200&w=200",
    quote: "The 30-day roadmap is gold. I followed it for 4 weeks and added 18k followers.",
  },
  {
    name: "Kavya Reddy", role: "Brand Lead · Fashion",
    avatar: "https://images.unsplash.com/photo-1764545973653-94c40d993495?crop=entropy&cs=srgb&fm=jpg&w=400",
    quote: "Worth ₹4999, costs ₹2,800. The competitor benchmark alone justifies the price.",
  },
];

const FAQ = [
  { q: "How long does the analysis take?", a: "60–120 seconds after payment. We scrape your profile, run a vision model on your posts, and generate the full report." },
  { q: "Does my Instagram need to be public?", a: "Yes. We only analyze public business profiles. Private accounts cannot be scanned." },
  { q: "What's in the ₹2,800 report?", a: "12 sections: brand audit, engagement quality, posting-time heatmap, hook suggestions, caption analysis, hashtag intelligence, competitor benchmark, content strategy, viral meter, audience analysis, 30-day roadmap, plus a downloadable PDF." },
  { q: "Is this the same as Instagram Insights?", a: "No. Instagram Insights gives you raw numbers. We give you AI-driven strategy and specific actions to improve." },
  { q: "Can I get a refund?", a: "Refunds within 24 hours of purchase if you're unhappy. Email hi@reelens.app and we'll process it manually." },
];

export default function Landing() {
  const nav = useNavigate();
  const [handle, setHandle] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [waOptIn, setWaOptIn] = useState(true);

  // Auto-clean an Instagram handle:
  //   – strips https://, http://, www., instagram.com/
  //   – strips leading @
  //   – strips trailing / and query params
  //   – lowercases (IG handles are always lowercase)
  //   – keeps only characters IG allows: a-z, 0-9, _ and .
  const cleanHandle = (raw) => {
    if (!raw) return "";
    let v = String(raw).trim().toLowerCase();
    v = v.replace(/^https?:\/\//, "");
    v = v.replace(/^www\./, "");
    v = v.replace(/^instagram\.com\//, "");
    v = v.replace(/^m\.instagram\.com\//, "");
    v = v.replace(/^@+/, "");
    v = v.split(/[/?#]/)[0]; // drop trailing slash / query / hash
    v = v.replace(/[^a-z0-9_.]/g, ""); // IG allows only these chars
    return v;
  };

  const submit = (e) => {
    e.preventDefault();
    const v = cleanHandle(handle);
    if (!v) return;
    sessionStorage.setItem("reelens_pending_handle", v);
    const em = email.trim();
    if (em) sessionStorage.setItem("reelens_pending_email", em);
    const ph = phone.replace(/[^\d]/g, "");
    if (ph) {
      sessionStorage.setItem("reelens_pending_phone", ph);
      sessionStorage.setItem("reelens_pending_wa_opt_in", waOptIn ? "1" : "0");
    }
    nav("/analyze");
  };

  return (
    <div className="min-h-screen bg-[#0F1117] text-white relative overflow-hidden">
      <AmbientBg variant="rich" />
      <Nav />

      {/* HERO */}
      <section className="relative z-10 px-6 pt-4 pb-16 sm:pt-16 sm:pb-32">
        <div className="max-w-6xl mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.05 }}
            className="font-display font-bold text-[26px] sm:text-6xl lg:text-[88px] leading-[1.1] sm:leading-[1.05] tracking-tight"
          >
            {/* Mobile: 2-line compact headline */}
            <span className="sm:hidden">
              AI <span className="text-grad-primary">Instagram Growth Audit</span> in 3 minutes
            </span>
            {/* Desktop: 3-line high-impact headline */}
            <span className="hidden sm:inline">
              Get an AI-Powered<br />
              <span className="text-grad-primary">Instagram Growth Audit</span><br />
              in 3 Minutes
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-6 sm:mt-8 text-base sm:text-xl text-white/70 max-w-3xl mx-auto leading-relaxed hidden sm:block"
          >
            We analyze your Instagram profile like a growth consultant — content quality,
            engagement, branding, hashtags, posting strategy, and competitors.
          </motion.p>

          <motion.form
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
            onSubmit={submit}
            className="mt-4 sm:mt-12 max-w-2xl mx-auto"
          >
            <div className="glass-strong rounded-2xl p-2 flex flex-col sm:flex-row sm:items-center gap-2">
              <div className="flex-1 flex items-center pl-3 sm:pl-4 min-w-0">
                <Instagram className="w-4 h-4 mr-2 shrink-0 text-white/40" />
                {/* Full URL prefix on ≥sm; just @ on mobile for space */}
                <span className="hidden sm:inline font-mono text-sm text-white/40 shrink-0">instagram.com/</span>
                <span className="sm:hidden font-mono text-base text-white/50 shrink-0 pr-0.5">@</span>
                <input
                  data-testid="hero-handle-input"
                  value={handle}
                  onChange={(e) => setHandle(cleanHandle(e.target.value))}
                  onPaste={(e) => {
                    e.preventDefault();
                    const pasted = e.clipboardData.getData("text");
                    setHandle(cleanHandle(pasted));
                  }}
                  placeholder="yourhandle"
                  autoComplete="off"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  inputMode="text"
                  aria-label="Instagram handle"
                  enterKeyHint="go"
                  className="flex-1 bg-transparent outline-none text-base sm:text-lg font-medium placeholder:text-white/30 py-3 pl-1 min-w-0"
                />
              </div>
              <button
                data-testid="hero-analyze-btn"
                type="submit"
                className="btn-primary text-sm sm:text-base whitespace-nowrap w-full sm:w-auto"
              >
                Analyze My Instagram →
              </button>
            </div>
            <div className="mt-3 glass rounded-2xl px-4 py-1 flex items-center gap-2">
              <span className="font-mono text-xs text-white/40 shrink-0">EMAIL</span>
              <input
                data-testid="hero-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                placeholder="you@brand.com  (optional — for report backup)"
                autoComplete="email"
                spellCheck={false}
                className="flex-1 bg-transparent outline-none text-sm placeholder:text-white/25 py-2.5 min-w-0"
              />
            </div>
            {/* WhatsApp opt-in — mobile + consent right on the hero form.
                Reduces one page-hop before user commits their contact info. */}
            <div className="mt-3 glass rounded-2xl px-4 py-1 flex items-center gap-2">
              <span className="font-mono text-xs text-white/40 shrink-0">🇮🇳 +91</span>
              <input
                data-testid="hero-phone-input"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/[^\d]/g, "").slice(0, 10))}
                type="tel"
                placeholder="WhatsApp number  (optional — get audit on WA)"
                autoComplete="tel"
                inputMode="numeric"
                className="flex-1 bg-transparent outline-none text-sm placeholder:text-white/25 py-2.5 min-w-0"
              />
            </div>
            <label
              data-testid="hero-wa-optin"
              className={`mt-2 flex items-start gap-2 cursor-pointer select-none px-1 transition-opacity ${phone ? "opacity-100" : "opacity-40 pointer-events-none"}`}
            >
              <input
                type="checkbox"
                checked={waOptIn}
                onChange={(e) => setWaOptIn(e.target.checked)}
                className="mt-0.5 h-3.5 w-3.5 rounded border-white/20 bg-white/5 accent-[#25D366] cursor-pointer"
              />
              <span className="text-[11px] sm:text-xs text-white/60 leading-snug text-left">
                <span className="text-[#25D366] font-semibold">WhatsApp</span> — I want to receive updates and my report on WhatsApp
              </span>
            </label>
            <div className="mt-4 flex flex-wrap justify-center items-center gap-2 text-xs text-white/40 font-mono">
              <span>Try:</span>
              {["nasa", "duolingo", "gymshark", "glossier"].map((s) => (
                <button
                  type="button" key={s}
                  onClick={() => setHandle(s)}
                  className="hover:text-white transition-colors"
                  data-testid={`hero-sample-${s}`}
                >
                  @{s}
                </button>
              ))}
            </div>
          </motion.form>

          {/* trust strip */}
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-16 flex flex-wrap justify-center items-center gap-x-10 gap-y-4 text-sm text-white/40"
          >
            <span className="flex items-center gap-2"><Star className="w-4 h-4 text-[#FF9F5A] fill-[#FF9F5A]" /> 4.9 / 5 from 2,400+ audits</span>
            <span className="flex items-center gap-2"><Lock className="w-4 h-4" /> No login required</span>
            <span className="flex items-center gap-2"><FileText className="w-4 h-4" /> Downloadable PDF</span>
            <span className="flex items-center gap-2"><Zap className="w-4 h-4 text-[#7B61FF]" /> 3-minute turnaround</span>
          </motion.div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="relative z-10 px-6 py-24 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-mono tracking-widest text-[#FF4D8D]">/ HOW IT WORKS</span>
            <h2 className="mt-3 font-display font-bold text-4xl sm:text-6xl tracking-tight">
              From handle to <span className="text-grad-primary">growth playbook</span><br />in three steps.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { n: "01", t: "Paste your handle", b: "Drop your Instagram URL or username. We instantly preview your profile." },
              { n: "02", t: "Pay ₹2,800 to unlock", b: "Razorpay checkout — UPI, GPay, PhonePe, or card. Instant unlock." },
              { n: "03", t: "Get your AI report", b: "12-section growth playbook + downloadable PDF in ~180 seconds." },
            ].map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }}
                className="glass rounded-2xl p-8 hover:border-white/20 transition-colors"
              >
                <div className="font-display font-bold text-5xl text-grad-primary">{s.n}</div>
                <h3 className="mt-4 font-display font-bold text-2xl">{s.t}</h3>
                <p className="mt-2 text-white/60 leading-relaxed">{s.b}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="sample" className="relative z-10 px-6 py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-xs font-mono tracking-widest text-[#7B61FF]">/ INSIDE THE REPORT</span>
            <h2 className="mt-3 font-display font-bold text-4xl sm:text-6xl tracking-tight">
              12 sections an agency<br />would charge ₹50,000 for.
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.5, delay: (i % 4) * 0.06 }}
                className="glass rounded-2xl p-6 hover:border-white/20 hover:bg-[#171923]/80 transition-all group"
              >
                <div className="w-11 h-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mb-4 group-hover:bg-grad-primary group-hover:border-transparent transition-all">
                  <f.icon className="w-5 h-5 text-white/80 group-hover:text-white" />
                </div>
                <h3 className="font-display font-bold text-lg mb-1.5">{f.title}</h3>
                <p className="text-sm text-white/55 leading-relaxed">{f.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="relative z-10 px-6 py-24 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-xs font-mono tracking-widest text-[#FF9F5A]">/ LOVED BY OPERATORS</span>
            <h2 className="mt-3 font-display font-bold text-4xl sm:text-5xl tracking-tight">What people say.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
                className="glass rounded-2xl p-6 hover:border-white/20 transition-colors"
              >
                <div className="flex gap-1 mb-3">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="w-3.5 h-3.5 text-[#FF9F5A] fill-[#FF9F5A]" />
                  ))}
                </div>
                <p className="text-white/85 leading-relaxed">&ldquo;{t.quote}&rdquo;</p>
                <div className="mt-5 flex items-center gap-3">
                  <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover border border-white/10" />
                  <div>
                    <div className="font-medium text-sm">{t.name}</div>
                    <div className="text-xs text-white/50">{t.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="relative z-10 px-6 py-24 border-t border-white/5">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-xs font-mono tracking-widest text-[#FF4D8D]">/ FAQ</span>
            <h2 className="mt-3 font-display font-bold text-4xl sm:text-5xl tracking-tight">Questions?</h2>
          </div>
          <div className="space-y-3">
            {FAQ.map((item, i) => (
              <FAQItem key={i} item={item} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="relative z-10 px-6 py-24 border-t border-white/5" data-testid="faq-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FF4D8D] mb-3">
              / FREQUENTLY ASKED
            </div>
            <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight">
              Questions, <span className="text-grad-primary">answered.</span>
            </h2>
          </div>
          <div className="space-y-3">
            {[
              {
                q: "What exactly do I get for ₹2,800?",
                a: "A 12-page premium AI audit (interactive web report + downloadable PDF). Covers 7 score dimensions, posting-time heatmap, hook suggestions, hashtag intel, competitor benchmark, content strategy, audience analysis, 30-day growth roadmap, and a Founder Action Board with 5 high-impact moves you can execute this week.",
              },
              {
                q: "How long does the audit take?",
                a: "Around 1-3 minutes. Once you pay, our AI pipeline reuses the preview scrape, runs Gemini 3 Flash vision over your visuals, and fires two GPT-5.4 passes in parallel (deep scoring + advanced recommendations). Your report is ready before your second chai.",
              },
              {
                q: "Do I need to give you Instagram access or password?",
                a: "Never. We only need your public profile URL. We use Apify (a trusted public scraping service) to fetch publicly visible posts. No login, no DMs read, no private data.",
              },
              {
                q: "Will the report email me a copy?",
                a: "Yes — if you enter your email at preview, we attach the full PDF to your completion email. You also get instant access on-screen and a downloadable PDF button on the report page.",
              },
              {
                q: "What if my profile is private?",
                a: "Reelens only audits public Instagram profiles. If your profile is private, our preview step will tell you upfront — no payment is taken. Switch to public temporarily, run the audit, then flip back if you like.",
              },
              {
                q: "Is this just AI-generated noise or actually useful?",
                a: "We benchmark against the top 50 profiles in your niche, ground everything in your actual scraped data (likes, comments, hashtags, captions, visual aesthetic), and produce specific actions — not generic advice. Sample report below to judge yourself.",
              },
              {
                q: "Can I get a refund if I'm not satisfied?",
                a: (
                  <>
                    Yes. If the analysis pipeline fails or your profile was private at the time of scan, you get a full automatic refund.{" "}
                    <a href="/legal/refund" className="text-[#FF4D8D] hover:underline">Refund policy →</a>
                  </>
                ),
              },
              {
                q: "Do you offer audits for multiple profiles?",
                a: "Yes — each ₹2,800 covers one profile. For 10+ profiles (agencies, brand portfolios), email us for bulk pricing. Drop a note to selectivecommunication@gmail.com.",
              },
            ].map((item, i) => (
              <details
                key={i}
                className="group glass rounded-2xl px-6 py-5 cursor-pointer transition-all hover:border-[#FF4D8D]/30"
                data-testid={`faq-item-${i}`}
              >
                <summary className="list-none flex items-start justify-between gap-4 font-display font-semibold text-lg text-white">
                  <span className="leading-snug">{item.q}</span>
                  <span className="text-[#FF4D8D] text-2xl leading-none flex-shrink-0 transition-transform group-open:rotate-45 select-none">
                    +
                  </span>
                </summary>
                <div className="mt-4 text-white/70 text-[15px] leading-relaxed">
                  {item.a}
                </div>
              </details>
            ))}
          </div>

          <div className="mt-10 text-center">
            <p className="text-sm text-white/50">
              Still have questions?{" "}
              <a href="/legal/contact" className="text-[#FF4D8D] hover:underline font-medium">
                Contact us →
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative z-10 px-6 py-24 border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center glass-strong tracking-border rounded-3xl p-12 sm:p-16 animate-pulse-glow">
          <h2 className="font-display font-bold text-4xl sm:text-6xl tracking-tight leading-tight">
            Ready for your<br />
            <span className="text-grad-primary">brutally honest audit?</span>
          </h2>
          <p className="mt-6 text-white/60 max-w-xl mx-auto">
            ₹2,800 · One-time payment · Instant unlock · Downloadable PDF.
          </p>
          <PromoNote className="mt-3" />
          <button
            data-testid="footer-cta-btn"
            onClick={() => nav("/analyze")}
            className="mt-10 btn-primary text-lg px-10 py-4"
          >
            Analyze My Instagram →
          </button>
        </div>
      </section>

      <footer className="relative z-10 px-6 py-10 border-t border-white/5 text-xs text-white/40 font-mono">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/60 mb-3">Product</div>
              <div className="flex flex-col gap-2">
                <a href="/analyze" className="hover:text-[#FF4D8D] transition-colors">Analyze My Instagram</a>
                <a href="/sample" className="hover:text-[#FF4D8D] transition-colors">Sample Report</a>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/60 mb-3">Resources</div>
              <div className="flex flex-col gap-2">
                <a href="/instagram-audit-framework" className="hover:text-[#FF4D8D] transition-colors" data-testid="footer-resources-framework">Instagram Audit Framework</a>
                <a href="/blog" className="hover:text-[#FF4D8D] transition-colors" data-testid="footer-resources-blog">Journal</a>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/60 mb-3">Legal</div>
              <div className="flex flex-col gap-2">
                <a href="/legal/terms" className="hover:text-[#FF4D8D] transition-colors">Terms</a>
                <a href="/legal/privacy" className="hover:text-[#FF4D8D] transition-colors">Privacy</a>
                <a href="/legal/refund" className="hover:text-[#FF4D8D] transition-colors">Refund Policy</a>
                <a href="/legal/contact" className="hover:text-[#FF4D8D] transition-colors">Contact</a>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/60 mb-3">Powered by</div>
              <div className="text-white/40 leading-relaxed">APIFY · GEMINI 3 FLASH · GPT-5.4 · RAZORPAY</div>
            </div>
          </div>
          <div className="pt-6 border-t border-white/5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 text-white/40">
            <span>© 2026 REELENS · Operated by Selective Communication, Mumbai</span>
            <a
              href="https://launchzone.co/p/reelensai?utm_source=reelensai_site&utm_medium=launch_badge&utm_campaign=lz_badge"
              target="_blank"
              rel="noopener"
              aria-label="Find us on LaunchZone"
              data-testid="launchzone-badge"
              className="inline-flex opacity-90 hover:opacity-100 transition-opacity"
            >
              <img
                src="https://launchzone.co/badge.svg?theme=light"
                alt="Find us on LaunchZone"
                width="154"
                height="54"
                loading="lazy"
              />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FAQItem({ item, index }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }} transition={{ duration: 0.4, delay: index * 0.05 }}
      className="glass rounded-xl overflow-hidden"
    >
      <button
        onClick={() => setOpen(!open)}
        data-testid={`faq-toggle-${index}`}
        className="w-full text-left px-6 py-5 flex justify-between items-center gap-4 hover:bg-white/5 transition-colors"
      >
        <span className="font-medium">{item.q}</span>
        <ChevronDown
          className={`w-4 h-4 flex-shrink-0 transition-transform ${open ? "rotate-180 text-[#FF4D8D]" : "text-white/40"}`}
        />
      </button>
      {open && (
        <div className="px-6 pb-5 text-white/65 leading-relaxed text-sm">{item.a}</div>
      )}
    </motion.div>
  );
}
